import type { ProfileCopy, ReferralPublicConfig } from 'types/referral';

/**
 * Textes d'origine de l'encart « Parrainage » de la page Mon compte, tels
 * qu'ils étaient écrits en dur dans `UserProfile`.
 *
 * Ils restent la référence : c'est ce que voit un parrain tant qu'aucune
 * campagne par défaut utilisable n'existe.
 */
export const PROFILE_COPY_FALLBACK: ProfileCopy = {
  heading: 'Parrainez vos proches',
  subtitle: 'Un ami parrainé = une session offerte',
  stepsIntro: 'La personne que vous parrainez doit :',
  steps: [
    'Se créer un compte via le lien envoyé',
    "Acheter n'importe quel crédit",
    'Vous recevrez ensuite automatiquement votre crédit offert',
  ],
};

/**
 * Textes à afficher dans l'encart parrainage, d'après la config publique.
 *
 * Seule LA campagne par défaut peut les personnaliser. Sans config (route en
 * erreur), sans campagne, sans campagne par défaut désignée, ou quand celle-ci
 * n'est plus utilisable — `campaigns` ne porte que les campagnes actives, dans
 * leur fenêtre et sous leur quota, donc une campagne finie n'y est plus — on
 * rend exactement les textes d'origine. Le parcours historique ne dépend ainsi
 * d'aucun réglage de back-office.
 *
 * Quand la campagne est là, chaque champ cascade : surcharge de la campagne,
 * puis valeurs par défaut du back-office, puis texte d'origine.
 */
export function resolveProfileCopy(config: ReferralPublicConfig | undefined): ProfileCopy {
  const defaultCampaignId = config?.defaultCampaignId ?? null;
  const campaign = defaultCampaignId
    ? config?.campaigns?.find((c) => c.id === defaultCampaignId)
    : undefined;

  if (!campaign) {
    return PROFILE_COPY_FALLBACK;
  }

  const defaults = config?.profileCopyDefaults;
  const override = campaign.profileCopy;

  // Le token n'a de valeur que si la campagne crédite le filleul. Sinon on le
  // retire, plutôt que de l'afficher tel quel ou d'inventer une quantité.
  const quantity =
    campaign.refereeCreditEnabled && campaign.refereeCreditQuantity
      ? String(campaign.refereeCreditQuantity)
      : '';
  const fill = (text: string): string =>
    text
      .replace(/\{\{\s*refereeCreditQuantity\s*\}\}/g, quantity)
      .replace(/\s{2,}/g, ' ')
      .trim();

  const steps = override?.steps ?? defaults?.steps ?? PROFILE_COPY_FALLBACK.steps;

  return {
    heading: fill(override?.heading ?? defaults?.heading ?? PROFILE_COPY_FALLBACK.heading),
    subtitle: fill(override?.subtitle ?? defaults?.subtitle ?? PROFILE_COPY_FALLBACK.subtitle),
    stepsIntro: fill(
      override?.stepsIntro ?? defaults?.stepsIntro ?? PROFILE_COPY_FALLBACK.stepsIntro,
    ),
    steps: [fill(steps[0] ?? ''), fill(steps[1] ?? ''), fill(steps[2] ?? '')],
  };
}
